import React, { type KeyboardEvent, type MouseEvent } from 'react';
import classNames from 'classnames';
import { HashRouter as Router, Route, Switch } from 'react-router-dom';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import StandardButton from '@atlaskit/button/standard-button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import GraphBarIcon from '@atlaskit/icon/glyph/graph-bar';
import PeopleIcon from '@atlaskit/icon/glyph/people';
import RoadmapIcon from '@atlaskit/icon/glyph/roadmap';
import ShipIcon from '@atlaskit/icon/glyph/ship';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { fg } from '@atlassian/jira-feature-gating';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import DropMenu, { DropMenuItem } from '@atlassian/jira-portfolio-3-common/src/drop-menu/index.tsx';
import { PRODUCT_ANALYTICS_EVENT_NAMES } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics/types';
import { preload as preloadDependenciesTab } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/dependencies/async';
import { preload as preloadReleasesTab } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/releases/async';
import { preload as preloadTeamsTab } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/teams/async';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import {
	PLAN_PAGES,
	PLAN_PATHS,
	redirectToPlanPage,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/window';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props } from './types';

const preloadAllTabs = () => {
	preloadReleasesTab();
	preloadTeamsTab();
	preloadDependenciesTab();
};

function ViewSwitcher({
	currentRoute,
	isMultiScenarioEnabled,
	isReadOnly,
	planScenarios,
	defaultOpen = false,
}: Props) {
	const { formatMessage } = useIntl();
	const routeNames = [
		{
			path: `/${PLAN_PATHS.ROADMAP}`,
			route: PLAN_PAGES.ROADMAP,
			Component: () => <FormattedMessage {...messages.timelineLabel} />,
			// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
			Icon: RoadmapIcon,
		},
		{
			path: `/${PLAN_PATHS.TEAMS}`,
			route: PLAN_PAGES.TEAMS,
			Component: () => <FormattedMessage {...messages.teamsLabel} />,
			// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
			Icon: PeopleIcon,
		},
		{
			path: `/${PLAN_PATHS.RELEASES}`,
			route: PLAN_PAGES.RELEASES,
			Component: () => <FormattedMessage {...messages.releasesLabel} />,
			// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
			Icon: ShipIcon,
		},
		{
			path: `/${PLAN_PATHS.DEPENDENCIES_REPORT}`,
			route: PLAN_PAGES.DEPENDENCIES_REPORT,
			Component: () => <FormattedMessage {...messages.dependenciesLabel} />,
			// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
			Icon: GraphBarIcon,
		},
	];

	const getHandlePageChange = (route: string) => {
		redirectToPlanPage(route);
	};

	const viewSwitcherBeforeIcon = () => {
		if (currentRoute === PLAN_PAGES.TEAMS) {
			return <PeopleIcon label="" />;
		}
		if (currentRoute === PLAN_PAGES.RELEASES) {
			return <ShipIcon label="" />;
		}
		if (currentRoute === PLAN_PAGES.DEPENDENCIES_REPORT) {
			return <GraphBarIcon label="" />;
		}
		return <RoadmapIcon label="" />;
	};

	const hasScenarios = !(
		isMultiScenarioEnabled !== true ||
		!isDefined(planScenarios) ||
		planScenarios.length < 1
	);

	return (
		<div
			data-testid="portfolio-3-portfolio.app-simple-plans.top.view-bar.view-switcher.button"
			onMouseEnter={preloadAllTabs}
		>
			<SpotlightTarget name="view-tab-switching">
				<DropMenu
					defaultOpen={defaultOpen}
					testId="portfolio-3-portfolio.app-simple-plans.top.view-bar.view-switcher.dropdown-menu"
					trigger={({ triggerRef, ...props }) => (
						<StandardButton
							ref={triggerRef}
							{...props}
							iconAfter={
								<ChevronDownIcon
									label={
										fg('jira-portfolio-button-name-part-3-a11y-fix')
											? formatMessage(messages.switchViewsDropdownLabel)
											: ''
									}
								/>
							}
							iconBefore={viewSwitcherBeforeIcon()}
						>
							<div
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
								className={classNames(styles.viewTrigger, {
									[styles.withScenarios]: hasScenarios,
									[styles.isReadOnly]: isReadOnly,
								})}
							>
								{/* @ts-expect-error - TS2769 - No overload matches this call. */}
								<Router hashType="noslash">
									<Switch>
										{routeNames.map((r) => (
											<Route key={r.path} path={r.path} component={r.Component} />
										))}
									</Switch>
								</Router>
							</div>
						</StandardButton>
					)}
				>
					{routeNames.map((r) => {
						const { route, Component, Icon } = r;
						const isSelected = currentRoute === route;
						return (
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
							<div className={isSelected ? styles.activeMenuItem : ''} key={`viewSwitcher${route}`}>
								<DropMenuItem
									elemBefore={<Icon label="" />}
									isSelected={isSelected}
									onClick={(_: KeyboardEvent | MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
										if (route === PLAN_PAGES.DEPENDENCIES_REPORT && analyticsEvent) {
											const [actionSubject, action] =
												PRODUCT_ANALYTICS_EVENT_NAMES.CLICKED_DEPENDENCY_TAB.split(' ');
											fireUIAnalytics(analyticsEvent.update({ action, actionSubject }));
										}
										getHandlePageChange(`/${route}`);
									}}
								>
									<Component />
								</DropMenuItem>
							</div>
						);
					})}
				</DropMenu>
			</SpotlightTarget>
		</div>
	);
}

export default ViewSwitcher;
